// ============================================================================
// SPARQL ADAPTER - BRIDGES EXISTING HOOKS TO NEW API CLIENT
// ============================================================================
// This adapter maintains the same interface as the old SPARQL client,
// but routes queries through the backend API with account and project context.

import type { SparqlResults } from '@/types/ontology'
import { SPARQL_PREFIXES } from '@/lib/ontology/namespaces'
import { sparqlApi } from './client'

// Current account and project context - must be set before making queries
let currentAccountId: string | null = null
let currentProjectId: string | null = null

export function setCurrentAccount(accountId: string | null) {
  currentAccountId = accountId
}

export function getCurrentAccountId(): string | null {
  return currentAccountId
}

export function setCurrentProject(projectId: string | null) {
  currentProjectId = projectId
}

export function getCurrentProjectId(): string | null {
  return currentProjectId
}

function ensureContext(): { accountId: string; projectId: string } {
  if (!currentAccountId) {
    throw new Error('No account selected. Please log in first.')
  }
  if (!currentProjectId) {
    throw new Error('No project selected. Please select a project first.')
  }
  return { accountId: currentAccountId, projectId: currentProjectId }
}

/**
 * Execute a SPARQL SELECT query
 */
export async function query(sparql: string, addPrefixes = true): Promise<SparqlResults> {
  const { accountId, projectId } = ensureContext()
  const fullQuery = addPrefixes ? `${SPARQL_PREFIXES}\n\n${sparql}` : sparql
  return sparqlApi.query(accountId, projectId, fullQuery)
}

/**
 * Execute a SPARQL ASK query
 */
export async function ask(sparql: string, addPrefixes = true): Promise<boolean> {
  const { accountId, projectId } = ensureContext()
  const fullQuery = addPrefixes ? `${SPARQL_PREFIXES}\n\n${sparql}` : sparql
  return sparqlApi.ask(accountId, projectId, fullQuery)
}

/**
 * Execute a SPARQL UPDATE query (INSERT, DELETE, etc.)
 */
export async function update(sparql: string, addPrefixes = true): Promise<void> {
  console.log('[sparql-adapter] update() called')
  console.log('[sparql-adapter] currentAccountId:', currentAccountId)
  console.log('[sparql-adapter] currentProjectId:', currentProjectId)

  const { accountId, projectId } = ensureContext()
  const fullQuery = addPrefixes ? `${SPARQL_PREFIXES}\n\n${sparql}` : sparql

  console.log('[sparql-adapter] Sending SPARQL UPDATE to API...')
  console.log('[sparql-adapter] Query preview:', fullQuery.substring(0, 150))

  try {
    await sparqlApi.update(accountId, projectId, fullQuery)
    console.log('[sparql-adapter] ✅ SPARQL UPDATE succeeded')
  } catch (error) {
    console.error('[sparql-adapter] ❌ SPARQL UPDATE failed:', error)
    throw error
  }
}

/**
 * Execute a SPARQL CONSTRUCT query
 * Note: Returns Turtle format as string
 */
export async function construct(sparql: string, addPrefixes = true): Promise<string> {
  const { accountId, projectId } = ensureContext()
  const fullQuery = addPrefixes ? `${SPARQL_PREFIXES}\n\n${sparql}` : sparql
  // For now, use query endpoint - backend could be extended to support CONSTRUCT
  const result = await sparqlApi.query(accountId, projectId, fullQuery)
  // Return stringified result for compatibility
  return JSON.stringify(result, null, 2)
}

/**
 * Clear all triples from a graph
 * Uses DROP + recreate for faster clearing (DROP is much faster than CLEAR)
 */
export async function clearGraph(graphUri?: string): Promise<void> {
  if (graphUri) {
    // For named graphs: DROP is faster than CLEAR
    // DROP removes the graph entirely, then we recreate it empty
    await update(`DROP GRAPH <${graphUri}>`, false)
    // Recreate the graph by inserting and removing a dummy triple
    await update(`INSERT DATA { GRAPH <${graphUri}> { <urn:dummy> <urn:dummy> <urn:dummy> } }`, false)
    await update(`DELETE DATA { GRAPH <${graphUri}> { <urn:dummy> <urn:dummy> <urn:dummy> } }`, false)
  } else {
    // For DEFAULT graph, use DROP DEFAULT
    // This is faster than CLEAR DEFAULT for large datasets
    await update(`DROP DEFAULT`, false)
  }
}

/**
 * Simple Turtle parser that extracts prefix declarations and converts to SPARQL INSERT DATA.
 * Handles basic Turtle syntax including prefixed names and full URIs.
 */
function turtleToSparql(turtle: string): string {
  const lines = turtle.split('\n')
  const prefixes: string[] = []
  const tripleLines: string[] = []

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#')) continue

    // Extract prefix declarations
    if (trimmed.toLowerCase().startsWith('@prefix') || trimmed.toLowerCase().startsWith('prefix')) {
      // Convert @prefix to SPARQL PREFIX format
      const prefixMatch = trimmed.match(/@?prefix\s+(\w*:)\s*<([^>]+)>\s*\.?/i)
      if (prefixMatch) {
        prefixes.push(`PREFIX ${prefixMatch[1]} <${prefixMatch[2]}>`)
      }
      continue
    }

    // Skip @base declarations for now
    if (trimmed.toLowerCase().startsWith('@base')) continue

    // Accumulate triple lines
    tripleLines.push(trimmed)
  }

  // Join triple lines and format as INSERT DATA
  const triplesBlock = tripleLines.join('\n  ')

  if (!triplesBlock.trim()) {
    throw new Error('No triples found in Turtle data')
  }

  return `${prefixes.join('\n')}\n\nINSERT DATA {\n  ${triplesBlock}\n}`
}

/**
 * Upload RDF data to the graph
 * Converts Turtle to SPARQL INSERT DATA and executes via the update endpoint.
 */
export async function uploadData(
  data: string,
  contentType: 'text/turtle' | 'application/ld+json' | 'application/rdf+xml' = 'text/turtle',
  _graphUri?: string
): Promise<void> {
  if (contentType !== 'text/turtle') {
    throw new Error(`Import of ${contentType} not yet supported. Please use Turtle format (.ttl).`)
  }

  try {
    const sparql = turtleToSparql(data)
    await update(sparql, false) // Don't add prefixes since they're in the converted SPARQL
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to import data'
    throw new Error(`Import failed: ${message}`)
  }
}

/**
 * Test connection to the SPARQL endpoint
 * For the new API, this tests if we're authenticated and have a project selected
 */
export async function testConnection(): Promise<boolean> {
  if (!currentAccountId || !currentProjectId) {
    return false
  }
  try {
    await ask('ASK { ?s ?p ?o }')
    return true
  } catch {
    return false
  }
}

// Re-export configuration types for compatibility
export interface SparqlClientConfig {
  accountId: string | null
  projectId: string | null
  // Legacy fields for compatibility (ignored)
  queryEndpoint?: string
  updateEndpoint?: string
  graphStoreEndpoint?: string
  username?: string
  password?: string
}

export function getConfig(): SparqlClientConfig {
  return { accountId: currentAccountId, projectId: currentProjectId }
}

export function configureSparqlClient(config: Partial<SparqlClientConfig>) {
  if (config.accountId !== undefined) {
    currentAccountId = config.accountId
  }
  if (config.projectId !== undefined) {
    currentProjectId = config.projectId
  }
  // Legacy config fields are ignored - connection is now managed via API auth
}
